import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";

function axiosMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ??
    (err as Error)?.message ??
    fallback
  );
}

type ApiResult = { success: boolean; message?: string };

type Props = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  submit: (payload: { reason?: string | null }) => Promise<ApiResult>;
};

export default function CancelInvoiceModal({
  show,
  onClose,
  onSuccess,
  submit,
}: Props) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) setReason("");
  }, [show]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await submit({ reason: reason.trim() || undefined });
      if (res.success) {
        toast.success(res.message ?? "Invoice cancelled.");
        onClose();
        await onSuccess();
      } else {
        toast.error(res.message ?? "Could not cancel invoice.");
      }
    } catch (err: unknown) {
      toast.error(axiosMessage(err, "Could not cancel invoice."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={show} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>Cancel invoice</ModalHeader>
      <ModalBody>
        <div className="alert alert-danger py-2 px-3 small mb-3">
          This will void the invoice outright. This action cannot be undone.
          Any advance settlements applied to it are automatically reversed,
          freeing that balance back up on the advance.
        </div>
        <Label className="form-label">
          Reason <span className="text-muted fw-normal">(optional)</span>
        </Label>
        <Input
          type="textarea"
          rows={3}
          placeholder="Why is this invoice being cancelled?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
        />
      </ModalBody>
      <ModalFooter>
        <Button color="light" disabled={submitting} onClick={handleClose}>
          Back
        </Button>
        <Button
          color="danger"
          disabled={submitting}
          onClick={() => void handleSubmit()}
        >
          {submitting ? "Cancelling…" : "Cancel invoice"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
